import type { DragEvent } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuShortcut, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { BoardCard } from "@/hooks/use-lead-board";
import { formatDateShort, formatDateTimeShort } from "@/lib/format";
import { BOARD_COLUMNS, BOARD_KEYS, DROP_ZONES, isOverdue, type BoardColumn, type BoardTarget } from "@/lib/lead-board";
import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";

interface LeadBoardCardProps {
  card: BoardCard;
  column: BoardColumn;
  dragging: boolean;
  dragProps: { draggable: boolean; onDragStart: (e: DragEvent) => void; onDragEnd: () => void };
  onMove: (target: BoardTarget) => void;
}

const MOVE_TARGETS: { id: BoardTarget; label: string; key?: string }[] = [
  ...DROP_ZONES.filter((z) => z.id === "no_answer"),
  { id: "to_call", label: "Skal ringe opp igjen", key: "2" },
  { id: "to_email", label: "Skal sende mail", key: "3" },
  { id: "booked", label: "Møte booket", key: "4" },
  ...DROP_ZONES.filter((z) => z.id !== "no_answer"),
];

function dueText(card: BoardCard, column: BoardColumn): string | null {
  if (!card.due_at) return null;
  if (column === "to_email") return `Send ${formatDateShort(card.due_at)}`;
  if (column === "booked") return `${formatDateTimeShort(card.due_at)}${card.host_name ? ` med ${card.host_name}` : ""}`;
  return formatDateTimeShort(card.due_at);
}

function contextText(card: BoardCard, column: BoardColumn): string | null {
  if (column === "to_contact") {
    if (card.status === "locked") return "Ditt nå";
    if (card.attempts_count > 0) return `Forsøk ${card.attempts_count + 1}`;
    return card.priority ? `Prioritet ${card.priority}` : null;
  }
  if (column === "to_call" && card.last_outcome === "email_sent" && card.last_attempt_at) {
    return `Mail sendt ${formatDateShort(card.last_attempt_at)}`;
  }
  return null;
}

/**
 * Kort på Leads-tavlen (VIB-3050). Dra kortet, eller velg det og trykk 1–6, eller bruk
 * «Flytt til». Dra og slipp er aldri eneste vei.
 */
export default function LeadBoardCard({ card, column, dragging, dragProps, onMove }: LeadBoardCardProps) {
  const movable = column !== "booked";
  const overdue = isOverdue(column, card.due_at);
  const due = dueText(card, column);
  const context = contextText(card, column);
  const columnLabel = BOARD_COLUMNS.find((c) => c.id === column)?.label ?? "";

  return (
    <div
      tabIndex={0}
      {...dragProps}
      onKeyDown={(e) => {
        if (!movable || e.target !== e.currentTarget || e.metaKey || e.ctrlKey || e.altKey) return;
        const target = BOARD_KEYS[e.key];
        if (target) {
          e.preventDefault();
          onMove(target);
        }
      }}
      aria-label={[card.name, columnLabel, card.municipality, due, overdue ? "forfalt" : null].filter(Boolean).join(", ")}
      className={cn(
        "space-y-1.5 border-[1.5px] border-foreground bg-card p-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
        dragProps.draggable && "cursor-grab",
        dragging && "opacity-50",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate font-semibold">{card.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {[card.municipality, card.employees != null ? `${card.employees} ansatte` : null].filter(Boolean).join(" · ")}
          </span>
        </span>
        {movable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" aria-label={`Flytt ${card.name} til`}>
                Flytt til
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Flytt til</DropdownMenuLabel>
              {MOVE_TARGETS.filter((t) => t.id !== column && !(column === "to_contact" && t.id === "pool")).map((t) => (
                <DropdownMenuItem key={t.id} onSelect={() => onMove(t.id)}>
                  {t.label}
                  {t.key && <DropdownMenuShortcut>{t.key}</DropdownMenuShortcut>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {column === "to_contact" && (card.contact_name || card.phone) && (
        <div className="flex flex-wrap items-center gap-x-2">
          {card.contact_name && <span>{card.contact_name}</span>}
          {card.phone && (
            <a href={`tel:${card.phone}`} className="underline" aria-label={`Ring ${card.contact_name ?? card.name}`}>
              {card.phone}
            </a>
          )}
        </div>
      )}

      {(due || context) && (
        <div className="flex flex-wrap items-center gap-x-2 text-xs">
          {due && <span className={cn(overdue ? TONE_TEXT.miss : "font-medium")}>{due}</span>}
          {overdue && <span className={TONE_TEXT.miss}>Forfalt</span>}
          {context && <span className="text-muted-foreground">{context}</span>}
        </div>
      )}

      {card.note && column !== "to_contact" && column !== "booked" && (
        <p className="line-clamp-2 text-xs text-muted-foreground">{card.note}</p>
      )}
    </div>
  );
}
